/**
 * Kanalpreise in Abschnitt 2 (Board „Preisautomatik · Einstellungen“): ein Aufschlag je Kanal,
 * unabhängig von der Preisautomatik, getrennt gespeichert und sofort an Channex beauftragt.
 */
import { problemText, useApiProblem } from '@/shared/api';
import { SurfaceLoadError, SurfaceLoading, SurfaceStaleHint } from '@/shared/components';
import { ChannelModifierCard } from './ChannelModifierCard';
import { useChannelPriceModifiers } from './useChannelPriceModifiers';

export function ChannelPriceModifiers({
  propertyId,
  currency = 'EUR',
}: {
  propertyId?: string;
  currency?: string;
}) {
  const { query, preview, update } = useChannelPriceModifiers(propertyId);
  const problem = useApiProblem(query.error ?? null);
  if (!propertyId) return null;
  const items = query.data ?? [];
  return (
    <section
      aria-labelledby="channel-prices-heading"
      className="bg-paper flex flex-col gap-3 rounded-lg p-4"
    >
      <span className="flex flex-col gap-0.5">
        <h3 id="channel-prices-heading" className="text-[15px] font-bold">
          Kanalpreise
        </h3>
        <span className="text-ink-soft text-xs">
          Aufschlag je Kanal. Gilt unabhängig von der Preisautomatik, wird getrennt gespeichert und
          sofort an Channex beauftragt.
        </span>
      </span>
      {query.isError && query.data && (
        <div className="overflow-hidden rounded-lg">
          <SurfaceStaleHint onRetry={() => void query.refetch()} retrying={query.isFetching} />
        </div>
      )}
      {/* Auch offline (pausiert) noch kein Stand – nicht „kein Kanal verbunden“. */}
      {query.isPending ? (
        <SurfaceLoading label="Kanalpreise werden geladen" rows={1} />
      ) : query.isError && !query.data ? (
        <div className="bg-card rounded-lg">
          <SurfaceLoadError
            message={problemText(problem, 'Die Kanalpreise konnten nicht geladen werden.')}
            onRetry={() => void query.refetch()}
            retrying={query.isFetching}
          />
        </div>
      ) : items.length === 0 ? (
        <p className="bg-card text-ink-soft rounded-lg p-3 text-sm">
          Für dieses Apartment ist kein Kanal verbunden.
        </p>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(280px,100%),1fr))] gap-2.5">
          {items.map((item) => (
            <ChannelModifierCard
              key={item.connectionStateId}
              item={item}
              currency={currency}
              // Ein Auftrag oder eine Vorschau zur Zeit; der Hinweis steht nur an der eigenen Karte.
              saving={Boolean(update.isPending || preview.isPending)}
              previewing={
                Boolean(preview.isPending) &&
                preview.variables?.connectionStateId === item.connectionStateId
              }
              saveError={
                update.variables?.connectionStateId === item.connectionStateId ? update.error : null
              }
              reloaded={query.dataUpdatedAt > update.submittedAt && !query.isFetching}
              onPreview={(increasePercent) =>
                preview.mutateAsync({
                  connectionStateId: item.connectionStateId,
                  increasePercent,
                  concurrency: item.concurrency,
                })
              }
              onSave={(increasePercent, concurrency) =>
                update.mutate({
                  connectionStateId: item.connectionStateId,
                  increasePercent,
                  concurrency,
                })
              }
            />
          ))}
        </div>
      )}
    </section>
  );
}
